import React from 'react';
import { Star, CheckCircle2, ShieldCheck, Leaf, Truck, StarHalf } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../AnimatedSection';

export default function HeroStyle4({ videoSrc, bgUrl, thumbnailSrc, title, subTitle }) {
  const pulseTransition = {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut"
  };

  const floatTransition = {
    duration: 3,
    repeat: Infinity,
    ease: "easeInOut",
    repeatType: "reverse"
  };

  return (
    <section
      className="cs_hero cs_style_4 position-relative"
      style={{
        backgroundImage: `url(${thumbnailSrc})`,
        paddingTop: 'clamp(60px, 15vw, 160px)',
        paddingBottom: 'clamp(60px, 10vw, 120px)',
        overflow: 'hidden'
      }}
    >
      <div className="container">
        <div className="row align-items-center cs_gap_y_40">
          <div className="col-lg-6">
            <div className="cs_hero_text position-relative z-index-2 text-center text-lg-start d-flex flex-column align-items-center align-items-lg-start">
              <AnimatedSection animation="fadeUp" delay={0.1}>
                {/* 1. Immediate Trust Block */}
                <div className="d-flex align-items-center justify-content-center justify-content-lg-start mb-3">
                  <div className="d-flex" style={{ color: '#FFD700' }}>
                    <Star fill="currentColor" size={20} />
                    <Star fill="currentColor" size={20} />
                    <Star fill="currentColor" size={20} />
                    <Star fill="currentColor" size={20} />
                    <StarHalf fill="currentColor" size={20} />
                  </div>
                  <span className="ms-2 cs_semibold" style={{ color: '#444', fontSize: 'clamp(14px, 2vw, 16px)' }}>
                    4.8/5 Rating from 10,000+ Men
                  </span>
                </div>

                <h1 className="cs_hero_title cs_semibold cs_primary_color mb-0" style={{ lineHeight: '1.2', fontSize: 'clamp(40px, 5vw, 60px)' }}>
                  {title}
                </h1>
                <div className="cs_height_20" />
                <p className="cs_hero_subtitle mb-0 text-center text-lg-start" style={{ color: '#444', fontSize: 'clamp(16px, 2.5vw, 22px)', lineHeight: '1.6' }}>
                  {subTitle}
                </p>
                <div className="cs_height_20 cs_height_lg_30" />

                {/* 2. Clear Value Proposition Checklist */}
                <ul className="cs_list cs_style_1 cs_mp0 mb-4 text-start d-inline-block" style={{ listStyle: 'none' }}>
                  <li className="d-flex align-items-center mb-2 cs_semibold text-dark" style={{ fontSize: 'clamp(15px, 2vw, 18px)', opacity: 0.9 }}>
                    <CheckCircle2 color="#68E0CF" size={20} className="me-2" /> Pure Certified Ayurvedic Formulation
                  </li>
                  <li className="d-flex align-items-center mb-2 cs_semibold text-dark" style={{ fontSize: 'clamp(15px, 2vw, 18px)', opacity: 0.9 }}>
                    <CheckCircle2 color="#68E0CF" size={20} className="me-2" /> Supports Natural Energy & Wellness
                  </li>
                  <li className="d-flex align-items-center mb-0 cs_semibold text-dark" style={{ fontSize: 'clamp(15px, 2vw, 18px)', opacity: 0.9 }}>
                    <CheckCircle2 color="#68E0CF" size={20} className="me-2" /> Quality Assured. Positive Feedback.
                  </li>
                </ul>
              </AnimatedSection>
              
              <div className="cs_height_30" />
              
              {/* 3. High-Converting CTA Button */}
              <AnimatedSection animation="fadeUp" delay={0.3}>
                <div className="d-inline-block w-100" style={{ maxWidth: '450px' }}>
                  <motion.a
                    href="#product-section"
                    className="cs_btn cs_style_1 cs_semibold cs_rounded_5 cs_pl_40 cs_pr_40 cs_pt_20 cs_pb_20 overflow-hidden w-100 d-flex justify-content-center"
                    animate={{ boxShadow: ['0 0 0px rgba(104,224,207,0)', '0 0 25px rgba(104,224,207,0.5)', '0 0 0px rgba(104,224,207,0)'] }}
                    transition={pulseTransition}
                    style={{ 
                      background: '#0b5e4b',
                      border: 'none',
                      color: '#fff',
                      textTransform: 'uppercase',
                      fontSize: 'clamp(16px, 2.5vw, 20px)'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('product-section')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <span>CLAIM YOUR OFFER NOW</span>
                  </motion.a>
                  
                  {/* 4. Friction-Removal Trust Badges */}
                  <div className="d-flex justify-content-center align-items-center mt-4 pt-3 flex-wrap gap-2 gap-md-3" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                    <div className="d-flex align-items-center text-muted px-2">
                      <ShieldCheck size={18} className="me-1" />
                      <span className="cs_semibold" style={{ fontSize: 'clamp(12px, 2vw, 14px)' }}>Secure Checkout</span>
                    </div>
                    <div className="d-flex align-items-center text-muted px-2 border-start border-end">
                      <Leaf size={18} className="me-1" />
                      <span className="cs_semibold" style={{ fontSize: 'clamp(12px, 2vw, 14px)' }}>GMP Certified</span>
                    </div>
                    <div className="d-flex align-items-center text-muted px-2">
                      <Truck size={18} className="me-1" />
                      <span className="cs_semibold" style={{ fontSize: 'clamp(12px, 2vw, 14px)' }}>Free Shipping</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>

          <div className="col-lg-6 position-relative z-index-2 mt-5 mt-lg-0">
            <AnimatedSection animation="slideInRight" delay={0.2}>
              <div className="cs_hero_img_box position-relative" style={{ padding: '20px' }}>
                <motion.img 
                  animate={{ y: [0, -15, 0] }}
                  transition={floatTransition}
                  className="cs_radius_15 cs_hero_main_img w-100" 
                  src="/images/digital-agency/hero-img.jpeg" 
                  alt="Ayumedix Wellness" 
                  style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.8)' }}
                />
                
                {/* 5. Polished Floating Badge */}
                <motion.div 
                  className="cs_hero_badge position-absolute" 
                  animate={{ y: [0, 10, 0] }}
                  transition={{ ...floatTransition, delay: 1 }}
                  style={{
                    bottom: '20px',
                    left: '10px',
                    width: 'clamp(100px, 25vw, 140px)',
                    height: 'auto',
                    padding: '10px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '15px',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.5)',
                    zIndex: 3
                  }}
                >
                  <img src="/trusted.png" alt="Trusted" className="w-100" />
                  <p className="cs_fs_12 cs_semibold text-center mb-0 mt-1" style={{ color: '#0b5e4b' }}>
                    Tested & Proven
                  </p>
                </motion.div>
                
                {/* Secondary Floating Highlight */}
                {/* <motion.div 
                  className="position-absolute d-flex align-items-center" 
                  animate={{ y: [0, -10, 0] }}
                  transition={{ ...floatTransition, delay: 0.5 }}
                  style={{
                    top: '20px',
                    right: '10px',
                    padding: '8px 15px',
                    background: '#fff',
                    borderRadius: '40px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    zIndex: 3
                  }}
                >
                  <div className="cs_accent_bg rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '24px', height: '24px' }}>
                    <CheckCircle2 size={14} color="#fff" />
                  </div>
                  <span className="cs_bold" style={{ fontSize: 'clamp(11px, 2.5vw, 14px)' }}>Doctor Recommended</span>
                </motion.div> */}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
      
      {/* Background Shapes */}
      {/* <div className="cs_hero_shape">
        <svg  width={458} height={475} viewBox="0 0 458 475" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.05" d="M2.35848 153.351L456.103 1.77286L370.156 472.382L2.35848 153.351Z" stroke="#0b5e4b" strokeWidth={2} />
          <circle cx="350" cy="350" r="100" stroke="#68E0CF" strokeWidth="1" opacity="0.1" fill="none" />
          <circle cx="350" cy="350" r="50" stroke="#68E0CF" strokeWidth="2" opacity="0.2" fill="none" />
        </svg>
      </div> */}
    </section>
  );
}
